import { dayOfWeekSchema, type Schedule, type TimeOfDay } from '@kidshub/contracts';
import { timeOfDayToMinutes } from '@kidshub/policy';
import { useTranslation } from 'react-i18next';

/**
 * The week, drawn the way time is drawn everywhere in this product.
 *
 * A horizontal strip per day, sage blocks for permitted hours. The dashboard's
 * "what happened today" and this screen's "when is it open" use the same shape
 * on purpose — a parent learns one picture and reads it in two places.
 *
 * The axis is the board's 06:00–21:00 unless a window falls outside it, in
 * which case it widens to take the window in. Drawing a window clipped would be
 * a lie about the setting the parent just made; drawing a whole day for every
 * family squeezes the hours that matter into a third of a phone.
 */

const MINUTES_PER_HOUR = 60;
const AXIS_STEP_HOURS = 3;
const DEFAULT_AXIS = { startHour: 6, endHour: 21 };
/** Roughly what one character of a segment label needs, as a share of a phone's track. */
const LABEL_SHARE_PER_CHARACTER = 0.028;

const axisFor = (schedule: Schedule) => {
  let { startHour, endHour } = DEFAULT_AXIS;
  for (const windows of Object.values(schedule.windowsByDay)) {
    for (const window of windows ?? []) {
      startHour = Math.min(
        startHour,
        Math.floor(timeOfDayToMinutes(window.start) / MINUTES_PER_HOUR),
      );
      endHour = Math.max(endHour, Math.ceil(timeOfDayToMinutes(window.end) / MINUTES_PER_HOUR));
    }
  }
  return {
    startHour: Math.floor(startHour / AXIS_STEP_HOURS) * AXIS_STEP_HOURS,
    endHour: Math.ceil(endHour / AXIS_STEP_HOURS) * AXIS_STEP_HOURS,
  };
};

/** `09:00` reads as `09`; a window that starts on the half hour keeps its minutes. */
const shortTime = (time: TimeOfDay) => (time.endsWith(':00') ? time.slice(0, 2) : time);

export const WeekStrip = ({ schedule }: { schedule: Schedule }) => {
  const { t } = useTranslation();
  const { startHour, endHour } = axisFor(schedule);
  const spanMinutes = (endHour - startHour) * MINUTES_PER_HOUR;
  const percentAt = (minutes: number) =>
    ((minutes - startHour * MINUTES_PER_HOUR) / spanMinutes) * 100;

  const axisHours: number[] = [];
  for (let hour = startHour; hour <= endHour; hour += AXIS_STEP_HOURS) {
    axisHours.push(hour);
  }

  return (
    <div>
      <div className="week">
        {dayOfWeekSchema.options.map((day) => (
          <div className="wk-row" key={day}>
            <span className="wk-day">{t(`day.${day}.short`)}</span>
            <div
              className="wk-track"
              role="img"
              aria-label={t('screenTime.dayWindowsLabel', {
                day: t(`day.${day}.long`),
                windows: schedule.windowsByDay[day]?.length ?? 0,
              })}
            >
              {(schedule.windowsByDay[day] ?? []).map((window) => {
                const left = percentAt(timeOfDayToMinutes(window.start));
                const width = percentAt(timeOfDayToMinutes(window.end)) - left;
                const label = `${shortTime(window.start)}–${shortTime(window.end)}`;
                return (
                  <span
                    key={`${window.start}-${window.end}`}
                    className="wk-seg num"
                    aria-hidden="true"
                    style={{ left: `${left}%`, width: `${width}%` }}
                  >
                    {width / 100 >= label.length * LABEL_SHARE_PER_CHARACTER ? label : null}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="wk-axis" aria-hidden="true">
        {axisHours.map((hour) => (
          <span
            key={hour}
            style={{ left: `${((hour - startHour) / (endHour - startHour)) * 100}%` }}
          >
            {String(hour).padStart(2, '0')}
          </span>
        ))}
      </div>
    </div>
  );
};
